// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PercorsiPage from './pages/PercorsiPage';
import LessonPage from './pages/LessonPage';
import QuizPage from './pages/QuizPage';
import CalcolatoriPage from './pages/CalcolatoriPage';
import GlossarioPage from './pages/GlossarioPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import AuthCallback from './pages/AuthCallback';
import GuidaBustaPaga from './pages/GuidaBustaPaga';
import GuidaKID from './pages/GuidaKID';
import ConfrontoBroker from './pages/ConfrontoBroker';
import ConfrontoContiDeposito from './pages/ConfrontoContiDeposito';
import AboutPage from './pages/AboutPage';
import PremiumPage from './pages/PremiumPage';
import PrivacyPage from './pages/PrivacyPage';
import DisclaimerPage from './pages/DisclaimerPage';
import NotFoundPage from './pages/NotFoundPage';
import Bonus2026Page from './pages/Bonus2026Page';
import SimulatorePortafoglio from './pages/SimulatorePortafoglio';
import BlogPage from './pages/BlogPage';
import BlogArticlePage from './pages/BlogArticlePage';
import ChiSiamoPage from './pages/ChiSiamoPage';
import FAQPage from './pages/FAQPage';
import TruffeFinanziariePage from './pages/TruffeFinanziariePage';
import RisorseHubPage from './pages/RisorseHubPage';
import RisorseBrokerPage from './pages/RisorseBrokerPage';
import RisorseContiDepositoPage from './pages/RisorseContiDepositoPage';
import RisorseLinksPage from './pages/RisorseLinksPage';
// Deployment 11 new pages
import CookiePolicyPage from './pages/CookiePolicyPage';
import TerminiPage from './pages/TerminiPage';
import GuidaDichiarazioneRedditi from './pages/GuidaDichiarazioneRedditi';
import QuizEspertoBustaPaga from './pages/QuizEspertoBustaPaga';
// Deployment 25 — guide dinamiche DB-driven
import GuidaDinamicaPage from './pages/GuidaDinamicaPage';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import NewsletterPostCalcBanner from './components/NewsletterBanner';
import OnboardingModal from './components/OnboardingModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        // Auto-claim referral code if stored in localStorage
        const pendingRef = localStorage.getItem('referral_code_pending');
        if (pendingRef && data.user) {
          try {
            await fetch(`${API_URL}/api/user/referral/claim`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ referral_code: pendingRef }),
            });
            localStorage.removeItem('referral_code_pending');
          } catch {}
        }
        return data.user;
      }
      return null;
    } catch (error) {
      console.error('Login failed:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#3D7A52] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#888780]">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Layout with Navbar and Footer
const Layout = ({ children, showFooter = true }) => {
  return (
    <div className="min-h-screen flex flex-col bg-[#1C1C1A]">
      <Navbar />
      <main className="flex-1">
        {children}
      </main>
      {showFooter && <Footer />}
      <OnboardingModal />
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Capture referral code from ?ref= param and store in localStorage immediately
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref && ref.length >= 6) {
      localStorage.setItem('referral_code_pending', ref);
    }
  }, [location.search]);

  // Check URL fragment for session_id (OAuth callback) - SYNCHRONOUS check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/login" element={<Layout showFooter={false}><LoginPage /></Layout>} />
      <Route path="/percorsi" element={<Layout><PercorsiPage /></Layout>} />

      {/* Calcolatori — with and without slug */}
      <Route path="/calcolatori" element={<Layout><CalcolatoriPage /></Layout>} />
      <Route path="/calcolatori/:calcSlug" element={<Layout><CalcolatoriPage /></Layout>} />

      <Route path="/glossario" element={<Layout><GlossarioPage /></Layout>} />
      <Route path="/guida-busta-paga" element={<Layout><GuidaBustaPaga /></Layout>} />
      <Route path="/guida-kid" element={<Layout><GuidaKID /></Layout>} />
      <Route path="/risorse/confronto-broker" element={<Layout><ConfrontoBroker /></Layout>} />
      <Route path="/risorse/confronto-conti-deposito" element={<Layout><ConfrontoContiDeposito /></Layout>} />

      {/* New static pages */}
      <Route path="/about" element={<Layout><AboutPage /></Layout>} />
      <Route path="/premium" element={<Layout><PremiumPage /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/privacy-policy" element={<Layout><PrivacyPage /></Layout>} />
      <Route path="/bonus-2026" element={<Layout><Bonus2026Page /></Layout>} />
      <Route path="/quiz" element={<Navigate to="/#quiz-section" replace />} />
      <Route path="/disclaimer" element={<Layout><DisclaimerPage /></Layout>} />

      {/* Deployment 11: Legal pages */}
      <Route path="/cookie-policy" element={<Layout><CookiePolicyPage /></Layout>} />
      <Route path="/termini" element={<Layout><TerminiPage /></Layout>} />
      <Route path="/termini-di-servizio" element={<Layout><TerminiPage /></Layout>} />

      {/* Deployment 11: New feature pages */}
      <Route path="/guida-dichiarazione-redditi" element={<Layout><GuidaDichiarazioneRedditi /></Layout>} />
      <Route path="/quiz/esperto-busta-paga" element={<Layout><QuizEspertoBustaPaga /></Layout>} />

      <Route path="/strumenti/simulatore-portafoglio" element={<Layout><SimulatorePortafoglio /></Layout>} />

      {/* Blog */}
      <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
      <Route path="/blog/:slug" element={<Layout><BlogArticlePage /></Layout>} />

      {/* Deployment 25 — Guide dinamiche DB-driven */}
      <Route path="/guide/:slug" element={<Layout><GuidaDinamicaPage /></Layout>} />

      {/* Nuove pagine statiche Sprint 17 */}
      <Route path="/chi-siamo" element={<Layout><ChiSiamoPage /></Layout>} />
      <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
      <Route path="/truffe-finanziarie" element={<Layout><TruffeFinanziariePage /></Layout>} />

      {/* Risorse Sprint 18 */}
      <Route path="/risorse" element={<Layout><RisorseHubPage /></Layout>} />
      {/* Deployment 17 — old broker/conti-deposito routes superseded by ConfrontoBroker / ConfrontoContiDeposito above */}
      <Route path="/risorse/link-utili" element={<Layout><RisorseLinksPage /></Layout>} />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><DashboardPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/lezione/:lessonId" element={<Layout><LessonPage /></Layout>} />
      <Route path="/quiz/:lessonId" element={
        <ProtectedRoute>
          <Layout showFooter={false}><QuizPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/profilo" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={<Layout showFooter={false}><AdminPage /></Layout>} />
      <Route path="/admin/*" element={<Layout showFooter={false}><AdminPage /></Layout>} />

      {/* 404 */}
      <Route path="*" element={<Layout><NotFoundPage /></Layout>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <CookieBanner />
        <NewsletterPostCalcBanner />
        <Toaster position="bottom-right" richColors duration={5000} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
